import React, { useState, useEffect } from 'react';
import PageThumbnail from './PageThumbnail';
import SelectedPagesPreview from './SelectedPagesPreview';

const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [sessionId, setSessionId] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]); // Array of {fileId, pageNumber, fileName}
  const [uploading, setUploading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);

  // Create a new session on component mount
  useEffect(() => {
    createSession();
  }, []);

  const createSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/session`, {
        method: 'POST',
      });
      const data = await response.json();
      setSessionId(data.sessionId);
      console.log('Session created:', data.sessionId);
    } catch (err) {
      setError('Failed to create session: ' + err.message);
    }
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('pdfs', file);
      });

      const response = await fetch(`${API_URL}/api/session/${sessionId}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFiles([...files, ...data.files]);
      
      // Add all pages from uploaded files to selectedPages (they start selected)
      const newSelectedPages = data.files.flatMap(file => 
        file.pages
          .filter(p => p.selected)
          .map(p => ({
            fileId: file.id,
            pageNumber: p.pageNumber,
            fileName: file.name
          }))
      );
      setSelectedPages([...selectedPages, ...newSelectedPages]);
    } catch (err) {
      setError('Failed to upload files: ' + err.message);
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const togglePageSelection = async (fileId, pageNumber) => {
    try {
      const file = files.find(f => f.id === fileId);
      const page = file.pages.find(p => p.pageNumber === pageNumber);

      await fetch(`${API_URL}/api/session/${sessionId}/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId,
          pageNumber,
          selected: !page.selected,
        }),
      });

      // Update local state
      setFiles(files.map(f => {
        if (f.id === fileId) {
          return {
            ...f,
            pages: f.pages.map(p =>
              p.pageNumber === pageNumber ? { ...p, selected: !p.selected } : p
            ),
          };
        }
        return f;
      }));

      // Update selected pages order
      if (page.selected) {
        // Removing from selection
        setSelectedPages(selectedPages.filter(
          sp => !(sp.fileId === fileId && sp.pageNumber === pageNumber)
        ));
      } else {
        // Adding to selection
        setSelectedPages([
          ...selectedPages,
          { fileId, pageNumber, fileName: file.name }
        ]);
      }
    } catch (err) {
      setError('Failed to update page selection: ' + err.message);
    }
  };

  const toggleAllPages = (fileId, selected) => {
    const file = files.find(f => f.id === fileId);
    file.pages.forEach(page => {
      fetch(`${API_URL}/api/session/${sessionId}/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId,
          pageNumber: page.pageNumber,
          selected,
        }),
      });
    });

    setFiles(files.map(f => {
      if (f.id === fileId) {
        return {
          ...f,
          pages: f.pages.map(p => ({ ...p, selected })),
        };
      }
      return f;
    }));

    // Update selected pages order
    if (selected) {
      // Add all pages in order
      const newPages = file.pages.map(p => ({
        fileId,
        pageNumber: p.pageNumber,
        fileName: file.name
      }));
      setSelectedPages([...selectedPages, ...newPages]);
    } else {
      // Remove all pages from this file
      setSelectedPages(selectedPages.filter(sp => sp.fileId !== fileId));
    }
  };

  const handleMerge = async () => {
    setMerging(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/session/${sessionId}/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pageOrder: selectedPages.map(sp => ({
            fileId: sp.fileId,
            pageNumber: sp.pageNumber,
          })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Merge failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'merged.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to merge PDFs: ' + err.message);
    } finally {
      setMerging(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await fetch(`${API_URL}/api/session/${sessionId}/file/${fileId}`, {
        method: 'DELETE',
      });

      setFiles(files.filter(f => f.id !== fileId));
      setSelectedPages(selectedPages.filter(sp => sp.fileId !== fileId));
    } catch (err) {
      setError('Failed to delete file: ' + err.message);
    }
  };

  const reorderSelectedPages = (newOrder) => {
    setSelectedPages(newOrder);
  };

  const removeFromSelectedPages = (fileId, pageNumber) => {
    setSelectedPages(selectedPages.filter(
      sp => !(sp.fileId === fileId && sp.pageNumber === pageNumber)
    ));
    // Also update the file's page selection
    togglePageSelection(fileId, pageNumber);
  };

  const totalSelectedPages = files.reduce(
    (total, file) => total + file.pages.filter(p => p.selected).length,
    0
  );

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>PDF Merger</h1>
        <p style={styles.subtitle}>Upload PDFs, select pages, and merge them into one file</p>
      </header>

      <div style={styles.uploadSection}>
        <label htmlFor="file-upload" style={styles.uploadButton}>
          {uploading ? 'Uploading...' : 'Upload PDF Files'}
        </label>
        <input
          id="file-upload"
          type="file"
          accept="application/pdf"
          multiple
          onChange={handleFileUpload}
          disabled={!sessionId || uploading}
          style={styles.fileInput}
        />
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {files.length > 0 && (
        <div style={styles.filesSection}>
          <SelectedPagesPreview
            sessionId={sessionId}
            selectedPages={selectedPages}
            onReorder={reorderSelectedPages}
            onRemove={removeFromSelectedPages}
          />

          <button
            onClick={handleMerge}
            disabled={merging || selectedPages.length === 0}
            style={{
              ...styles.mergeButton,
              ...(merging || selectedPages.length === 0 ? styles.mergeButtonDisabled : {}),
            }}
          >
            {merging ? 'Merging...' : `Merge & Download (${selectedPages.length} pages)`}
          </button>

          <div style={styles.summary}>
            <p>{files.length} file(s) uploaded, {totalSelectedPages} page(s) selected</p>
          </div>

          {files.map((file) => (
            <div key={file.id} style={styles.fileCard}>
              <div style={styles.fileHeader}>
                <h3 style={styles.fileName}>{file.name}</h3>
                <button
                  onClick={() => handleDeleteFile(file.id)}
                  style={styles.deleteButton}
                >
                  Remove
                </button>
              </div>

              <div style={styles.fileControls}>
                <button
                  onClick={() => toggleAllPages(file.id, true)}
                  style={styles.controlButton}
                >
                  Select All
                </button>
                <button
                  onClick={() => toggleAllPages(file.id, false)}
                  style={styles.controlButton}
                >
                  Deselect All
                </button>
              </div>

              <div style={styles.pagesGrid}>
                {file.pages.map((page) => (
                  <PageThumbnail
                    key={page.pageNumber}
                    sessionId={sessionId}
                    fileId={file.id}
                    pageNumber={page.pageNumber}
                    isSelected={page.selected}
                    onToggle={() => togglePageSelection(file.id, page.pageNumber)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {files.length === 0 && !uploading && (
        <div style={styles.emptyState}>
          <p>No files uploaded yet. Click the button above to get started!</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '36px',
    color: '#333',
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  uploadSection: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  uploadButton: {
    display: 'inline-block',
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
  fileInput: {
    display: 'none',
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid #f5c6cb',
  },
  filesSection: {
    marginTop: '20px',
  },
  summary: {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    textAlign: 'center',
    fontWeight: '500',
  },
  fileCard: {
    backgroundColor: 'white',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  fileHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  fileName: {
    margin: 0,
    fontSize: '18px',
    color: '#333',
  },
  deleteButton: {
    padding: '6px 12px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  fileControls: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px',
  },
  controlButton: {
    padding: '6px 12px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  pagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
    gap: '15px',
    marginTop: '10px',
  },
  mergeButton: {
    width: '100%',
    padding: '15px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: '500',
    marginTop: '20px',
  },
  mergeButtonDisabled: {
    backgroundColor: '#6c757d',
    cursor: 'not-allowed',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
  },
};

export default App;
